<template>
  <rs-side-panel ref="sidePanel">
    <template slot="header">Sakinleri Ekle</template>
    <template slot="body">
      <v-form
        ref="form"
        class="add-residents-to-batch-form"
        @submit.prevent="handleFormSubmit"
        v-if="formData.resident_ids.length > 0"
      >
        <div class="add-residents-to-batch-content">
          <section class="batch-drawer-section">
            <div class="batch-drawer-section__header">
              <h4>Bildirim Detayları</h4>
              <p>
                {{ formData.resident_ids.length }} adet sakin için gönderim
                ayarlarını seçin.
              </p>
            </div>

            <v-row dense>
              <v-col cols="12" md="6">
                <rs-select
                  label="Bildirim Türü"
                  v-model="formData.notification_channel_id"
                  :items="notificationChannelListIds"
                  :rules="[rules.required]"
                  @change="handleChannelChange"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
                v-if="formData.notification_channel_id && formData.send_now"
              >
                <rs-select-notification-template
                  v-model="formData.template_id"
                  @change:item="handleTemplateUpdate"
                  :channels="selectedChannelList"
                  :preselect-system-id="preselectSystemTemplateId"
                  :rules="[rules.required]"
                  required
                  sendable
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
                v-if="formData.notification_channel_id && !formData.send_now"
              >
                <rs-select
                  label="Toplu Gönderim Seçin"
                  :items="batchList"
                  v-model="batchId"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12">
                <v-switch
                  class="mt-1"
                  inset
                  :label="
                    formData.send_now ? 'Şimdi Gönder' : 'Toplu Bildirime Ekle'
                  "
                  v-model="formData.send_now"
                />
              </v-col>
            </v-row>
          </section>

          <section class="batch-drawer-section">
            <div class="batch-drawer-section__header">
              <h4>Önizleme</h4>
              <p>
                Mesaj metni bu gönderim için düzenlenebilir. Şablon kalıcı
                olarak değiştirilmez.
              </p>
            </div>

            <div class="batch-variable-list" v-if="formData.send_now">
              <div class="batch-variable-list__title">Değişkenler</div>
              <VariableCloud
                :system-id="
                  selectedTemplate ? selectedTemplate.system_id : null
                "
                @select="addVariable"
              />
            </div>

            <v-row dense v-if="formData.send_now" class="mt-2">
              <v-col cols="12">
                <rs-textarea
                  label="Gönderim Mesajı"
                  v-model="formData.body"
                  :rules="[rules.required, rules.maxLength(formData.body, 500)]"
                  rows="5"
                  ref="body"
                  required
                />
              </v-col>
            </v-row>

            <div class="batch-preview-title">Örnek Mesaj</div>
            <div class="batch-preview-box">
              <pre v-if="previewText">{{ previewText }}</pre>
              <span v-else>{{ previewPlaceholder }}</span>
            </div>
          </section>

          <section
            v-if="selectedResidentList.length > 0"
            class="batch-drawer-section"
          >
            <div
              class="batch-drawer-section__header batch-drawer-section__header--with-badge"
            >
              <div>
                <h4>Bildirim Listesi</h4>
                <p>Gönderime dahil edilecek sakinler.</p>
              </div>

              <span class="batch-recipient-count-badge">
                {{ selectedResidentList.length }}
              </span>
            </div>

            <div class="batch-resident-list">
              <div
                class="batch-resident-list__item"
                v-for="resident in selectedResidentList"
                :key="resident.id"
              >
                <div class="batch-resident-list__identity">
                  <strong>{{ resident.name }}</strong>
                  <span>{{ formatResidentLocation(resident) }}</span>
                </div>

                <div
                  class="batch-resident-list__debt"
                  v-if="resident.debt_balance !== null"
                >
                  Açık Borç: {{ formatDebtBalance(resident.debt_balance) }}
                </div>
              </div>
            </div>
          </section>
        </div>

        <rs-form-buttons
          hide-submit
          :is-loading="isLoading"
          @cancel="hide()"
          @submit="handleFormSubmit"
          :save-close-label="formData.send_now ? 'Gönder' : undefined"
        />
      </v-form>

      <p v-else>En az bir sakin seçili olmalıdır.</p>
    </template>
  </rs-side-panel>
</template>

<script>
import { mapGetters } from "vuex";

import { notificationTemplateVariables } from "@/core/data/options";
import { hasForm } from "@/view/mixins";
import VariableCloud from "../VariableCloud";

const SMS_CHANNEL_ID = 2;
const EMAIL_CHANNEL_ID = 4;
const MOBILE_PUSH_CHANNEL_ID = 5;
const BANK_ACCOUNT_VARIABLE = "{BankaHesapIBANKodları}";
const TODAY_VARIABLE = "{Bugün}";
const CLUSTER_VARIABLE = "{TYA}";
const ALL_DEBTS_VARIABLE = "{TümBorçlar}";
const ALL_DEBTS_BY_TYPE_SYSTEM_ID = 7;

export default {
  name: "AddResidentsToBatchForm",
  mixins: [hasForm],
  components: {
    VariableCloud,
  },
  computed: {
    ...mapGetters(["cluster", "clusterId", "notificationChannelListIds"]),
    selectedChannelList() {
      if (!this.formData.notification_channel_id) {
        return [];
      }

      return [this.formData.notification_channel_id];
    },
    previewText() {
      if (!this.formData.body) {
        return "";
      }

      return this.renderExampleText(this.formData.body);
    },
    previewPlaceholder() {
      if (!this.formData.send_now) {
        return "Seçilen mevcut toplu bildirimin mesaj içeriği kullanılacak.";
      }

      return "Şablon seçildiğinde mesaj önizlemesi burada gösterilecek.";
    },
  },
  watch: {
    "formData.send_now"(sendNow) {
      if (sendNow) {
        this.batchId = null;
        return;
      }

      this.formData.template_id = null;
      this.formData.body = null;
      this.selectedTemplate = null;
    },
    "formData.body"(body) {
      if (this.hasBankAccountVariable(body)) {
        this.loadSmsBankAccounts();
      }
    },
  },
  data() {
    return {
      formData: {
        resident_ids: [],
        notification_channel_id: 2,
        send_now: true,
        body: null,
      },
      batchList: [],
      batchId: null,
      preselectSystemTemplateId: null,
      extraParams: {},
      selectedResidentList: [],
      selectedTemplate: null,
      variables: notificationTemplateVariables,
      bankAccountList: [],
      bankAccountClusterId: null,
      isBankAccountLoading: false,
      previewDebtText: null,
      isPreviewDebtLoading: false,
    };
  },
  methods: {
    show(residentIds, systemTemplateId, extraParams, selectedResidents = []) {
      // get the unique resident ids
      this.formData.resident_ids = residentIds.filter((item, index, array) => {
        return array.indexOf(item, index + 1) === -1;
      });
      this.preselectSystemTemplateId = systemTemplateId || null;
      this.extraParams = extraParams || {};
      this.formData.notification_channel_id = SMS_CHANNEL_ID;
      this.formData.send_now = true;
      this.formData.template_id = null;
      this.formData.body = null;
      this.batchId = null;
      this.selectedTemplate = null;
      this.previewDebtText = null;
      this.selectedResidentList = this.buildSelectedResidentList(
        this.formData.resident_ids,
        selectedResidents
      );
      this.loadBatchList();
      this.$refs.sidePanel.show();
    },
    hide() {
      this.$refs.sidePanel.hide();
    },
    handleCancelClick() {
      this.hide();
    },
    buildSelectedResidentList(residentIds, selectedResidents) {
      return residentIds.map((residentId) => {
        const resident =
          selectedResidents.find((item) => item.id === residentId) || {};

        return {
          id: residentId,
          name: resident.name || `Sakin #${residentId}`,
          block: resident.block || "",
          door_number: resident.door_number || "",
          phone: resident.phone || "",
          debt_balance:
            resident.debt_balance === undefined ? null : resident.debt_balance,
        };
      });
    },
    formatDebtBalance(value) {
      return Number(value || 0).toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatResidentLocation(resident) {
      return [resident.block, resident.door_number].filter(Boolean).join("-");
    },
    getSampleResident() {
      return this.selectedResidentList.length > 0
        ? this.selectedResidentList[0]
        : null;
    },
    getVariableExample(variable) {
      const sampleResident = this.getSampleResident();

      if (variable.value === TODAY_VARIABLE) {
        return new Date().toLocaleDateString("tr-TR");
      }

      if (variable.value === CLUSTER_VARIABLE) {
        return this.cluster?.name || variable.example;
      }

      if (variable.value === ALL_DEBTS_VARIABLE) {
        return this.previewDebtText || variable.example;
      }

      if (variable.value === BANK_ACCOUNT_VARIABLE) {
        return this.bankAccountList.length
          ? this.renderBankAccountText()
          : variable.example;
      }

      if (sampleResident) {
        if (variable.value === "{SakinAdıSoyadı}") {
          return sampleResident.name;
        }

        if (variable.value === "{BağımsızBölüm}") {
          return (
            this.formatResidentLocation(sampleResident) || variable.example
          );
        }

        if (variable.value === "{SakinTelefonu}" && sampleResident.phone) {
          return sampleResident.phone;
        }

        if (
          variable.value === "{SakinGüncelBakiyesi}" &&
          sampleResident.debt_balance !== null
        ) {
          return `${this.formatDebtBalance(sampleResident.debt_balance)}TL(B)`;
        }
      }

      return variable.example || variable.value;
    },
    renderExampleText(message) {
      let preview = message || "";

      for (const index in this.variables) {
        const variable = this.variables[index];

        preview = preview.replace(
          new RegExp(this.escapeForRegExp(variable.value), "gi"),
          this.getVariableExample(variable)
        );
      }

      return preview;
    },
    getTemplateBodyForSelectedChannel(template) {
      if (!template) {
        return "";
      }

      if (template.multiple_body) {
        if (this.formData.notification_channel_id === SMS_CHANNEL_ID) {
          return template.body_sms || template.body || "";
        }

        if (this.formData.notification_channel_id === EMAIL_CHANNEL_ID) {
          return template.body_email || template.body || "";
        }

        if (this.formData.notification_channel_id === MOBILE_PUSH_CHANNEL_ID) {
          return template.body_mobile_push || template.body || "";
        }
      }

      return template.body || template.description || "";
    },
    escapeForRegExp(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    hasBankAccountVariable(text) {
      return typeof text === "string" && text.includes(BANK_ACCOUNT_VARIABLE);
    },
    formatIban(value) {
      const iban = (value || "").replace(/\s+/g, "").trim();

      if (!iban) {
        return "";
      }

      const parts = [iban.slice(0, 2), iban.slice(2, 4)];

      for (let index = 4; index < iban.length; index += 4) {
        parts.push(iban.slice(index, index + 4));
      }

      return parts.filter(Boolean).join(" ");
    },
    getBankAccountDisplayName(account) {
      return (
        account.title || account.name || account.bank_name || "Banka Hesabı"
      );
    },
    renderBankAccountText() {
      return this.bankAccountList
        .filter((account) => !!account.bank_iban)
        .map(
          (account) =>
            `${this.getBankAccountDisplayName(account)}: ${this.formatIban(
              account.bank_iban
            )}`
        )
        .join(", ");
    },
    formatSmsMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    async loadPreviewDebtText(force = false) {
      const sampleResident = this.getSampleResident();

      if (
        !sampleResident ||
        (this.previewDebtText && !force) ||
        (this.isPreviewDebtLoading && !force)
      ) {
        return this.previewDebtText;
      }

      this.isPreviewDebtLoading = true;

      try {
        const response = await this.$api.query(
          `income/residents/${sampleResident.id}`,
          {
            params: {
              per_page: 2000,
            },
          }
        );
        const totalsByIncomeType = {};
        const list = response?.data?.data || [];

        for (const item of list) {
          if (!item.debt_id) {
            continue;
          }

          const amount = Number(item.total_balance || 0);

          if (amount <= 0) {
            continue;
          }

          const incomeType = item.income_type || "Borç";
          totalsByIncomeType[incomeType] =
            (totalsByIncomeType[incomeType] || 0) + amount;
        }

        this.previewDebtText = Object.keys(totalsByIncomeType)
          .sort()
          .map((incomeType) => {
            return `${incomeType} (${this.formatSmsMoney(
              totalsByIncomeType[incomeType]
            )}TL)`;
          })
          .join(", ");
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isPreviewDebtLoading = false;
      }

      return this.previewDebtText;
    },
    async loadSmsBankAccounts(force = false) {
      if (
        !this.clusterId ||
        (this.bankAccountClusterId === this.clusterId && !force) ||
        (this.isBankAccountLoading && !force)
      ) {
        return this.bankAccountList;
      }

      this.isBankAccountLoading = true;

      try {
        const response = await this.$api.query("bank-accounts", {
          params: {
            cluster_id: this.clusterId,
            per_page: 200,
            order_by: "name",
            order: "asc",
            can_used_for_sms: true,
            residents_can_see: true,
            can_collect_money: true,
          },
        });

        this.bankAccountList =
          response && response.data && response.data.data
            ? response.data.data
            : [];
        this.bankAccountClusterId = this.clusterId;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isBankAccountLoading = false;
      }

      return this.bankAccountList;
    },
    handleChannelChange() {
      this.formData.template_id = null;
      this.formData.body = null;
      this.selectedTemplate = null;
      this.batchId = null;
      this.previewDebtText = null;
      this.loadBatchList();
    },
    insertValueToMessage(value) {
      const currentValue = this.formData.body || "";
      const activeElement = document.activeElement;

      if (activeElement && activeElement.tagName === "TEXTAREA") {
        const start = currentValue.substr(0, activeElement.selectionStart);
        const end = currentValue.substr(activeElement.selectionStart);
        this.formData.body = start + value + end;
        return;
      }

      this.formData.body = currentValue + value;
    },
    async addVariable(variable) {
      if (variable.value === BANK_ACCOUNT_VARIABLE) {
        await this.loadSmsBankAccounts();
      }

      this.insertValueToMessage(variable.value);
    },
    loadBatchList() {
      if (!this.formData.notification_channel_id) {
        return;
      }

      this.batchList = [];
      const params = {
        notification_channel_id: [this.formData.notification_channel_id],
      };
      if (this.clusterId) {
        params.cluster_id = this.clusterId;
      }

      this.isLoading = true;

      this.$api
        .query("batch-notifications", { params })
        .then((response) => {
          if (response.data && response.data.data) {
            this.batchList = response.data.data;
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    handleFormSubmit() {
      if (this.isLoading || !this.$refs.form.validate()) {
        this.$toast.error("Lütfen tüm gerekli alanları doldurunuz");
        return false;
      }

      if (!this.formData.send_now) {
        return this.handleFormSubmitAdd();
      }

      return this.handleFormSubmitSend();
    },
    handleFormSubmitAdd() {
      const formData = Object.assign({}, this.formData);
      this.isLoading = true;

      this.$api
        .post(`batch-notifications/${this.batchId}/residents`, formData)
        .then(() => {
          this.$toast.success("Eklendi.");
          this.$emit("saved");
          this.hide();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleFormSubmitSend() {
      const formData = Object.assign({}, this.extraParams, this.formData);
      formData.cluster_id = this.clusterId;

      if (
        this.selectedTemplate &&
        formData.body ===
          this.getTemplateBodyForSelectedChannel(this.selectedTemplate)
      ) {
        delete formData.body;
      } else if (this.selectedTemplate && !formData.name) {
        formData.name = `${
          this.selectedTemplate.name
        } ${new Date().toLocaleDateString("tr-TR")}`;
      }

      this.isLoading = true;

      this.$api
        .post(`batch-notifications/create-send`, formData)
        .then((response) => {
          this.$toast.success(
            `${response.data.data.sent} adet bildirimin gönderimi başladı`
          );
          this.$emit("saved");
          this.hide();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async handleTemplateUpdate(selectedTemplate) {
      this.selectedTemplate = selectedTemplate || null;
      this.previewDebtText = null;
      this.formData.body = this.getTemplateBodyForSelectedChannel(
        this.selectedTemplate
      );

      if (this.hasBankAccountVariable(this.formData.body)) {
        await this.loadSmsBankAccounts();
      }

      if (this.selectedTemplate?.system_id === ALL_DEBTS_BY_TYPE_SYSTEM_ID) {
        await this.loadPreviewDebtText(true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.add-residents-to-batch-form {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 96px);
}

.add-residents-to-batch-content {
  flex: 1 1 auto;
  padding-bottom: 24px;
}

.batch-drawer-section {
  background: #fff;
  border: 1px solid #e6e8ef;
  border-radius: 10px;
  margin-bottom: 16px;
  padding: 18px 18px 12px;
}

.batch-drawer-section__header {
  border-bottom: 1px solid #eef0f5;
  margin: 0 -18px 16px;
  padding: 0 18px 14px;

  h4 {
    color: #24262d;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
  }

  p {
    color: #7a7f8f;
    font-size: 13px;
    margin: 4px 0 0;
  }
}

.batch-drawer-section__header--with-badge {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.batch-recipient-count-badge {
  align-items: center;
  background: #1976d2;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  height: 30px;
  justify-content: center;
  min-width: 30px;
  padding: 0 10px;
}

.batch-preview-box {
  background: #f8f9fc;
  border: 1px dashed #d8dce8;
  border-radius: 8px;
  color: #555b6d;
  min-height: 88px;
  padding: 14px 16px;

  pre {
    color: #24262d;
    font-family: inherit;
    margin: 0;
    white-space: pre-wrap;
  }
}

.batch-preview-title {
  color: #7a7f8f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin: 8px 0;
  text-transform: uppercase;
}

.batch-variable-list {
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 12px 12px 6px;
}

.batch-variable-list__title {
  color: #7a7f8f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.batch-resident-list {
  border: 1px solid #eef0f5;
  border-radius: 8px;
  max-height: 280px;
  overflow-y: auto;
}

.batch-resident-list__item {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-height: 46px;
  padding: 10px 14px;

  & + & {
    border-top: 1px solid #eef0f5;
  }
}

.batch-resident-list__identity {
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    color: #24262d;
    font-size: 13px;
    line-height: 1.25;
  }

  span {
    color: #7a7f8f;
    font-size: 12px;
    line-height: 1.25;
    margin-top: 2px;
  }
}

.batch-resident-list__debt {
  color: #555b6d;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
}

@media screen and (max-width: 600px) {
  .batch-resident-list__item {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
